// ADDED: the Friends page - search for collectors by username, answer the requests waiting
// on you, and manage the people you are already connected to.
//
// Friendship is two-sided: sending a request does nothing until the other person accepts.
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

import axios, { getErrorMessage } from '../../api/axios';
import { FRIENDS_CHANGED_EVENT } from '../Components/NotificationBell'; // ADDED

export default function Friends() {
    const [friends, setFriends] = useState([]);
    const [incoming, setIncoming] = useState([]);
    const [outgoing, setOutgoing] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    // A short note under the search box after an action ("Request sent to redline_hunter")
    const [notice, setNotice] = useState('');

    // Search state, debounced the same way the Models page does it
    const [searchInput, setSearchInput] = useState('');
    const [results, setResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);

    const loadFriends = useCallback(async () => {
        try {
            const response = await axios.get('/friends');
            setFriends(response.data.friends);
            setIncoming(response.data.incoming);
            setOutgoing(response.data.outgoing);
            setError('');
        } catch (err) {
            setError(getErrorMessage(err, 'Could not load your friends.'));
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { loadFriends(); }, [loadFriends]);

    // Wait 400ms after the last keystroke, and do not search on one character
    useEffect(() => {
        const term = searchInput.trim();
        if (term.length < 2) { setResults([]); return undefined; }

        const timer = setTimeout(() => {
            setIsSearching(true);
            axios.get('/friends/search', { params: { q: term } })
                .then((response) => setResults(response.data.users))
                .catch(() => setResults([]))
                .finally(() => setIsSearching(false));
        }, 400);

        return () => clearTimeout(timer);
    }, [searchInput]);

    // After any change both the lists and the current search results are refreshed, so a
    // row cannot sit there still offering "Add" for someone you just added.
    const refresh = async (message) => {
        setNotice(message || '');
        await loadFriends();
        // ADDED: lets the bell in the nav bar update now instead of within the minute
        window.dispatchEvent(new Event(FRIENDS_CHANGED_EVENT));
        const term = searchInput.trim();
        if (term.length >= 2) {
            const response = await axios.get('/friends/search', { params: { q: term } });
            setResults(response.data.users);
        }
    };

    const handleAdd = async (collector) => {
        try {
            const response = await axios.post('/friends', { userId: collector.id });
            // "accepted" comes back when they had already asked you - asking back completes it
            await refresh(response.data.status === 'accepted'
                ? `You and ${collector.username} are now friends.`
                : `Request sent to ${collector.username}.`);
        } catch (err) {
            setNotice(getErrorMessage(err, 'Could not send that request.'));
        }
    };

    const handleRespond = async (request, accept) => {
        try {
            await axios.put(`/friends/requests/${request.id}`, { accept });
            await refresh(accept
                ? `You and ${request.user.username} are now friends.`
                : `Request from ${request.user.username} declined.`);
        } catch (err) {
            setNotice(getErrorMessage(err, 'Could not answer that request.'));
        }
    };

    const handleRemove = async (collector, isCancel = false) => {
        // Removing a friend is not undoable without them accepting again, so it is confirmed
        const question = isCancel
            ? `Cancel your request to ${collector.username}?`
            : `Remove ${collector.username} from your friends? They will no longer see your shared collections.`;
        if (!window.confirm(question)) return;

        try {
            await axios.delete(`/friends/${collector.id}`);
            await refresh(isCancel ? 'Request cancelled.' : `${collector.username} removed.`);
        } catch (err) {
            setNotice(getErrorMessage(err, 'Could not remove that friend.'));
        }
    };

    // One row shape for every list on the page, so the three sections line up visually
    const collectorRow = (collector, actions) => (
        <div key={collector.id}
             className="flex items-center gap-3 px-4 py-3"
             style={{ borderTop: '1px solid var(--border-subtle)' }}>
            <div className="w-9 h-9 rounded-full bg-brand-flame text-white font-display font-bold
                            flex items-center justify-center shrink-0 uppercase">
                {(collector.username || '?').charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
                <p className="cv-strong truncate">{collector.username}</p>
                <p className="text-xs cv-muted truncate">{collector.name}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">{actions}</div>
        </div>
    );

    const section = (title, count, children) => (
        <div className="cv-card overflow-hidden">
            <div className="px-4 pt-4 pb-3 flex items-center gap-2">
                <h2 className="cv-display text-lg">{title}</h2>
                {count > 0 &&
                    <span className="bg-brand-flame text-white font-display font-bold text-xs px-2 py-0.5 rounded-lg">
                        {count}
                    </span>
                }
            </div>
            {children}
        </div>
    );

    return (
        <>
        <section className="cv-page pt-8 pb-6 px-4 sm:px-8">
            <div className="max-w-4xl mx-auto text-center space-y-2">
                <p className="cv-eyebrow">The garage next door</p>
                <h1 className="cv-display text-4xl sm:text-5xl">Friends</h1>
                <p className="text-sm cv-muted max-w-xl mx-auto">
                    Add other collectors by username and browse the collections they have shared.
                    Only collections you mark public are visible to your friends.
                </p>
            </div>
        </section>

        <section className="cv-page px-4 sm:px-8 pb-10 max-w-4xl mx-auto w-full space-y-5">
            {error && <p className="cv-alert-error text-center">{error}</p>}

            {/* Search */}
            <div className="cv-card overflow-hidden">
                <div className="p-4 space-y-3">
                    <div className="relative">
                        <input
                            id="friendSearch"
                            value={searchInput}
                            onChange={(e) => { setSearchInput(e.target.value); setNotice(''); }}
                            type="text"
                            autoComplete="off"
                            placeholder="Find a collector by username"
                            className="cv-input rounded-full pl-11"
                        />
                        <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 cv-muted">
                                <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                            </svg>
                        </div>
                    </div>
                    {notice && <p className="text-xs cv-accent">{notice}</p>}
                    {isSearching && <p className="text-xs cv-muted">Searching...</p>}
                    {!isSearching && searchInput.trim().length >= 2 && results.length === 0 &&
                        <p className="text-xs cv-muted">No collector with that username.</p>
                    }
                </div>

                {results.map((collector) => collectorRow(collector,
                    collector.relationship === 'friends'
                        ? <Link to={`/friends/${collector.id}`} className="cv-btn cv-btn-quiet text-sm">View</Link>
                    : collector.relationship === 'requested'
                        ? <span className="text-xs cv-muted">Request sent</span>
                    : collector.relationship === 'awaiting-your-reply'
                        ? <span className="text-xs cv-accent">Asked to add you</span>
                    : <button onClick={() => handleAdd(collector)} className="cv-btn cv-btn-outline text-sm">Add</button>
                ))}
            </div>

            {isLoading && <p className="text-center cv-muted py-10">Loading...</p>}

            {/* Requests waiting on me - first, because they need an answer */}
            {!isLoading && incoming.length > 0 && section('Requests', incoming.length,
                incoming.map((request) => collectorRow(request.user, (
                    <>
                        <button onClick={() => handleRespond(request, true)} className="cv-btn cv-btn-primary text-sm">Accept</button>
                        <button onClick={() => handleRespond(request, false)} className="cv-btn cv-btn-quiet text-sm">Decline</button>
                    </>
                )))
            )}

            {/* My friends */}
            {!isLoading && section('Your friends', friends.length,
                friends.length === 0
                    ? <p className="px-4 pb-4 text-sm cv-muted">
                        Nobody yet. Search for a collector above and send them a request.
                      </p>
                    : friends.map((collector) => collectorRow(collector, (
                        <>
                            <Link to={`/friends/${collector.id}`} className="cv-btn cv-btn-outline text-sm">View</Link>
                            <button onClick={() => handleRemove(collector)}
                                    aria-label={`Remove ${collector.username}`}
                                    className="cv-muted hover:text-brand-flame px-1.5 transition duration-300">
                                &times;
                            </button>
                        </>
                    )))
            )}

            {/* Requests I sent that have not been answered */}
            {!isLoading && outgoing.length > 0 && section('Waiting on them', outgoing.length,
                outgoing.map((request) => collectorRow(request.user, (
                    <button onClick={() => handleRemove(request.user, true)} className="cv-btn cv-btn-quiet text-sm">
                        Cancel
                    </button>
                )))
            )}
        </section>
        </>
    );
}
